import React, { useCallback, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useNotification } from 'hooks'
import { useJobCategoryQuery } from '../../api'
import { InfoItems } from 'components/core-ui/InfoItems'
import { Box, CircularProgress } from '@mui/material'
import style from '../../jobs.modules.scss'
import ActionComponents from '../../components/ActionComponents'
import EditJobCategoryModal from '../EditJobCategory/EditJobCategoryModal'
import { DeleteConfirmationModal } from 'components/core-ui/DeleteConfirmationModal'
import { formatUIDateTime } from 'utils/helpers'

const JobCategoryDetails: React.FC = () => {
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false)
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false)
    const [shouldRefreshData, setShouldRefreshData] = useState<boolean>(false)
    const { canEdit, canDelete } = { canEdit: true, canDelete: false };

    const { id } = useParams<{ id: string }>()
    const { showNotification } = useNotification()
    const { data: jobCat, isLoading, refetch: refetchJobCat } = useJobCategoryQuery(showNotification, id, shouldRefreshData)

    const openEditModal = useCallback(() => setIsEditModalOpen(true), [])
    const closeEditModal = useCallback(() => setIsEditModalOpen(false), [])

    const openDeleteModal = useCallback(() => setIsDeleteModalOpen(true), [])
    const closeDeleteModal = useCallback(() => setIsDeleteModalOpen(false), [])

    if (isLoading) return <CircularProgress color="primary" style={{ margin: '40px auto' }} disableShrink />
    if (!jobCat) return <div>Job category not found</div>

    return (
        <div className={style.mainContainer}>
            {canEdit && (
                <EditJobCategoryModal
                    isModalOpen={isEditModalOpen}
                    onModalClose={closeEditModal}
                    jobCatId={jobCat.id}
                    onSuccess={refetchJobCat}
                />
            )}
            {canDelete && (
                <DeleteConfirmationModal
                    isModalOpen={isDeleteModalOpen}
                    actionUrl='/iam/management-tools/job-categories/:id'
                    content='Are you sure you want to delete this job category?'
                    closeModal={closeDeleteModal}
                    itemId={jobCat.id}
                    setShouldRefreshData={setShouldRefreshData}
                />
            )}

            <Box className={style.flexContainer}>
                <Box sx={{ flex: 1 }}>
                    <InfoItems
                        itemsPerRow={1}
                        items={[
                            { name: jobCat?.name, label: 'Name:' },
                            { name: jobCat?.code, label: 'ISIM Code:' },
                            { name: jobCat?.description, label: 'Description:' },
                            { name: jobCat?.active ? "Active" : "Inactive", label: 'Status:' },
                            { name: jobCat?.createdBy, label: 'Created By:' },
                            { name: formatUIDateTime(jobCat?.createdDate), label: 'Created Date:' },
                            { name: jobCat?.lastModifiedBy, label: 'Modified By:' },
                            { name: formatUIDateTime(jobCat?.lastModifiedDate), label: 'Modified Date:' },
                            { name: jobCat.id.toString(), label: 'Category ID:' }
                        ]}
                        title={"Job Category"}
                        actionComponents={<ActionComponents canDelete={false} canEdit={canEdit} openDeleteModal={openDeleteModal} openEditModal={openEditModal} />}
                    />
                </Box>
            </Box>
        </div>
    )
}

export default JobCategoryDetails